import {
  ChatHttpConfig,
  ChatProvider,
  ChatWebSocketConfig,
  NotificationsProvider,
  ChatRedirection,
  NotificationsFormattersOverride,
} from "@eworkit/messenger";
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useFeatureFlags } from "@/components/FeatureFlags";
import { ConsultantProfileContext, UserContext } from "@/components/User";
import { FEATURE_FLAG } from "@/data/feature-flags";
import { getChatAuthToken, xAuthToken } from "@/data/session";
import { getVariable, Variables } from "@/env";
import { useFormats } from "@/hooks/Formats";
import { router } from "@/views/router";

import { SessionContext } from "../Session";
import { imageResolver } from "./imageResolver";

type ChatConfig = { http: ChatHttpConfig; webSocket: ChatWebSocketConfig };

function buildWSUrl(url: string) {
  return url.startsWith("https") ? url.replace("https", "wss") : url.replace("http", "ws");
}

const APP_API = getVariable(Variables.APP_API);
const WS_API_URL = `${buildWSUrl(APP_API || "")}/api/message/ws`;

const CHAT_API = getVariable(Variables.CHAT_API);
const CHAT_API_WS = `${buildWSUrl(CHAT_API || "")}/ws`;

export const MessengerProvider: FC = ({ children }) => {
  const { currentCompanyId } = useContext(ConsultantProfileContext);
  const { authenticated, payload } = useContext(SessionContext);
  const { sessionUser } = useContext(UserContext);
  const { isFeatureEnabled } = useFeatureFlags();

  /**
   *  COMMON
   */
  const formats = useFormats();
  const timezone = sessionUser?.settings.zone;

  const dateFormatters = useMemo<NotificationsFormattersOverride>(
    () => ({
      formatDate: (date: Date) => formats.formatDate(date.toISOString()),
      formatDateTime: (date: Date) => formats.formatDateTime(date.toISOString()),
      formatTime: (date: Date) => formats.formatTime(date.toISOString()),
      formatPeriod: (dateFrom: Date, dateTo: Date) =>
        formats.formatPeriod(dateFrom.toISOString(), dateTo.toISOString()),
      timezone,
    }),
    [timezone, formats.dateFormat, formats.dateTimeFormat, formats.timeFormat],
  );

  /**
   *  NOTIFICATIONS
   */
  const [notificationsToken, setNotificationsToken] = useState<string | undefined>();

  const fetchNotificationsToken = useCallback(async () => {
    const newToken = await xAuthToken({ companyId: currentCompanyId! });
    setNotificationsToken(newToken);
  }, [currentCompanyId]);

  useEffect(() => {
    fetchNotificationsToken();
  }, [currentCompanyId]);

  const notificationsConfig = useMemo(
    () => ({
      http: {
        config: { baseURL: APP_API },
      },
      webSocket: { url: WS_API_URL },
    }),
    [],
  );

  /**
   *  CHAT
   */
  const [chatToken, setChatToken] = useState<string | undefined>();

  useEffect(() => {
    setChatToken(getChatAuthToken());
  }, [payload]);

  const chatConfig: ChatConfig = useMemo(
    () => ({
      http: {
        config: {
          baseURL: CHAT_API,
        },
      },
      webSocket: {
        url: CHAT_API_WS,
        enabled: isFeatureEnabled(FEATURE_FLAG.MESSAGE_CENTER) && authenticated,
      },
    }),
    [authenticated],
  );

  const chatRedirections = useMemo<ChatRedirection[]>(
    () => [
      {
        entity: "JOB_REQUEST_APPLICATION",
        pathTo: jobApplicationId => {
          return router.get("job-applications.details.details", { jobApplicationId }).path;
        },
      },
    ],
    [],
  );

  return (
    <NotificationsProvider
      http={notificationsConfig.http}
      webSocket={notificationsConfig.webSocket}
      xAuthToken={notificationsToken}
      desktopNotifications={{ enabled: false, iconPath: "/favicons/mstile-150x150.png" }}
      imageResolver={imageResolver}
      formattersOverride={dateFormatters}>
      <ChatProvider
        http={chatConfig.http}
        webSocket={chatConfig.webSocket}
        authToken={chatToken}
        system="sp"
        redirections={chatRedirections}
        formattersOverride={dateFormatters}
        features={{
          startConversation: isFeatureEnabled(FEATURE_FLAG.CHAT_CONVERSATION_WITHOUT_CONTEXT),
        }}>
        {children}
      </ChatProvider>
    </NotificationsProvider>
  );
};
